import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import Audit from "src/components/Audit";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import ModalSide from "src/components/Modal/styles";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { ICoin, IComponentCoinModals, IRefCoinModals } from "./interface";
import coinSchema from "./schemas";

const ModalCoins: React.ForwardRefRenderFunction<IRefCoinModals, IComponentCoinModals> = ({
	paginationRef
}, ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const [selectedCoin, setSelectedCoin] = useState<ICoin>({} as ICoin);

	const actionButtonDelete = (row: ICoin) => {
		confirm(
			translate("Exclusão de moeda"),
			`${translate("Deseja realmente excluir a moeda")}: ${row.moa001Descricao} ?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Coins/Delete",
					urlParameters: {
						id: row.moa001Id
					}
				});
	
				addToast({
					color: "green",
					description: translate("Moeda excluída com sucesso"),
					title: translate("Sucesso")
				});

				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir Moeda"), addToast);
			},
			true
		);
	};

	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { handleSubmit : handleSubmitAddOrChange, register : registerAddOrChange, reset : resetAddOrChange, setError : setErrorAddOrChange, formState: { errors : errorsAddOrChange }, clearErrors: clearErrorsAddOrChange } = useForm();
	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();

			await coinSchema(translate).validate(data, {
				abortEarly: false
			});

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "Coins/Insert",
					body: {
						...data
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "Coins/Update",
					body: {
						...data,
						moa001Id: selectedCoin.moa001Id
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Moeda adicionada com sucesso") : translate("Moeda alterada com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar moeda") : translate("Erro ao alterar moeda"), addToast);
		}
	};
	const actionButtonAdd = () => {
		resetAddOrChange(); 
		setSelectedCoin({} as ICoin); 
		setIsAdd(true); 
		modalAddOrChange.current?.openModal();
	};
	const actionButtonChange = (row: ICoin) => {
		resetAddOrChange(); 
		setSelectedCoin(row); 
		setIsAdd(false); 
		modalAddOrChange.current?.openModal(); 
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));
    
	return (
		<>	
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar Moeda") : translate("Alterar Moeda"),
					icon: {
						name: isAdd ? "plus" : "pencil"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						alignItems="center"
						justify="flex-end"
					>
						<Audit data={selectedCoin} type="pop"/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							name="moa001Descricao"
							label={translate("Descrição")}
							maxLength={200}
							defaultValue={selectedCoin.moa001Descricao}
							error={errorsAddOrChange.moa001Descricao}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							name="moa001Sigla"
							label={translate("Sigla")}
							maxLength={10}
							defaultValue={selectedCoin.moa001Sigla}
							error={errorsAddOrChange.moa001Sigla}
							required
						/>
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(ModalCoins);
